import { emailValidation } from '@eneco/common/validation/emailRegex';
import {
  $CompanyFormatTypeDto,
  $OrderSwitchReasonDto,
  $PriceSimulationMeterSubTypeDto,
  $PriceSimulationMeterTypeDto,
  $SolarPanelInstallationDateDto,
} from '@eneco/dc-be/client/schemas.gen';
import { GenderDto, OrderSwitchReasonDto, PaymentMethodDto, CompanyFormatTypeDto } from '@eneco/dc-be/client/types.gen';
import { OrderManagementRendering } from '@sitecore/types/OrderManagement';
import { addMonths, startOfMonth, subYears } from 'date-fns';
import * as yup from 'yup';

import { isValidCompanyNumber } from '../../../components/CompanyNumberInput';
import { isValidFormattedIban } from '../../../components/IbanNumberInput';
import { phoneValidationScheme } from '../../../components/PhoneNumberInputField';
import { isValidGezinsbondNumber } from '../../../utils/isValidGezinsbondNumber';
import { loadPriceSimulationState } from '../../price-simulation/utils/priceSimulationLocalStorage';
import { ELEC_DETAILS_STEP_COMPENSATION_OPTIONS } from '../steps/ElectricityDetailsStep';
import { ORDER_MANAGEMENT_PAYMENT_METHODS } from '../steps/SelectPaymentMethodStep';
import {
  CHOOSE_START_DATE_QUESTION_NAME,
  NEW_METER_CONNECTION,
  TAKE_OVER_METER_CONNECTION_OLD_INHABITANT,
} from '../utils/constants';
import { getStartDateDateBounds, parseOrderSwitchReason } from '../utils/orderManagementDateBounds';
import { isCompanyNumberRequested } from '../utils/vatStatus';

export const PROFESSIONAL_USER_OPTION_YES = 'Yes';
const companyTypeOptions = $CompanyFormatTypeDto.enum;
const EAN_CODE_OF_METER_OPTIONS = ['EAN', 'MeterNumber', 'Unknown'];

const minEighteen = subYears(new Date(), 18);
const earliestDateOfBirth = new Date(1753, 0, 1);
export const getOrderManagementSchema = (fields: OrderManagementRendering['fields']) =>
  yup.object({
    boilerMaintenance: yup.string().required(fields.boilerMaintenanceStep.errorText.value),
    serviceProposal: yup.string().required(fields.assistanceService.errorText.value),
    hasSelectedGasProduct: yup.boolean().optional(),
    myEnecoAccount: yup
      .string()
      .required(fields.myEnecoAccountStep?.errorText?.value)
      .oneOf(
        [fields.myEnecoAccountStep.yesLabel.value, fields.myEnecoAccountStep.noLabel.value],
        fields.myEnecoAccountStep?.errorText?.value,
      ),
    personalDetailsStep: yup.object({
      gender: yup.string<GenderDto>().required(fields.personalDetailsStep.birthDateFormField.value.requiredMessage),
      firstName: yup.string().required(fields.personalDetailsStep.firstNameFormField.value.requiredMessage),
      lastName: yup.string().required(fields.personalDetailsStep.lastNameFormField.value.requiredMessage),
      dateOfBirth: yup
        .date()
        .transform((value, rawValue) => {
          if (typeof rawValue === 'string' && rawValue) {
            const parsed = new Date(rawValue);
            return isNaN(parsed.getTime()) ? value : parsed;
          }
          return value;
        })
        .typeError(fields.personalDetailsStep.birthDateFormField.value.requiredMessage)
        .min(earliestDateOfBirth, fields.personalDetailsStep.birthDateFormField.value.validationMinValueMessage)
        .max(minEighteen, fields.personalDetailsStep.birthDateFormField.value.validationMessage),
      phoneNumber: phoneValidationScheme({
        requiredMessage: fields.personalDetailsStep.phoneNumberFormField.value.requiredMessage,
        validationMessage: fields.personalDetailsStep.phoneNumberFormField.value.validationMessage,
        isRequired: true,
      }),
      email: emailValidation({
        emailMessage: fields.personalDetailsStep.emailFormField.value.validationMessage,
        requiredMessage: fields.personalDetailsStep.emailFormField.value.requiredMessage,
      }),
      receiveInvoicesThroughMail: yup.boolean().default(false),
      professionalUser: yup.string().oneOf(
        fields.personalDetailsStep.professionalUserFormField.value.options.map(option => option.name),
        fields.personalDetailsStep.professionalUserFormField.value.requiredMessage,
      ),
      companyName: yup.string().when('professionalUser', {
        is: PROFESSIONAL_USER_OPTION_YES,
        then: schema => schema.required(fields.personalDetailsStep.companyNameFormField.value.requiredMessage),
      }),
      VATNumber: yup.string().when(['professionalUser', 'vatStatus'], {
        is: (professionalUser: string, vatStatus: string) =>
          professionalUser === PROFESSIONAL_USER_OPTION_YES && !isCompanyNumberRequested(vatStatus),
        then: schema =>
          schema
            .required(fields.personalDetailsStep.companyNumberFormField.value.requiredMessage)
            .test(
              'pattern',
              fields.personalDetailsStep.companyNumberFormField?.value.validationMessage,
              function (value) {
                return isValidCompanyNumber(value);
              },
            )
            .max(12, fields.personalDetailsStep.companyNumberFormField?.value.validationMaxValueMessage),
        otherwise: schema => schema.notRequired(),
      }),
      companyType: yup.string<CompanyFormatTypeDto>().when('professionalUser', {
        is: PROFESSIONAL_USER_OPTION_YES,
        then: schema =>
          schema
            .oneOf(companyTypeOptions, fields.personalDetailsStep.companyTypeList.value.requiredMessage)
            .required(fields.personalDetailsStep.companyTypeList.value.requiredMessage),
        otherwise: schema => schema.notRequired(),
      }),
      vatStatus: yup.string().when('professionalUser', {
        is: PROFESSIONAL_USER_OPTION_YES,
        then: schema =>
          schema
            .oneOf(
              fields.personalDetailsStep.vatTypesList?.value?.options?.map(option => option.value),
              fields.personalDetailsStep.vatTypesList?.value?.requiredMessage,
            )
            .required(fields.personalDetailsStep.vatTypesList?.value?.requiredMessage),
        otherwise: schema => schema.notRequired(),
      }),
      isCocontractor: yup.boolean().default(false),
    }),
    deliveryAddressStep: yup.object({
      correspondenceAddress: yup.object({
        isCorrespondenceAddressSameAsDeliveryAddress: yup
          .boolean()
          .default(true)
          .oneOf(
            [true, false],
            fields.addressStep.sameAddressNotification?.value?.content ?? '[not in sitecore]: Je moet een keuze maken.',
          ),
        street: yup.string().when('isCorrespondenceAddressSameAsDeliveryAddress', {
          is: false,
          then: schema => schema.required(fields.addressStep.streetFormField.value.requiredMessage),
          otherwise: schema => schema.notRequired(),
        }),
        houseNumber: yup.number().when('isCorrespondenceAddressSameAsDeliveryAddress', {
          is: false,
          then: schema =>
            schema
              .transformEmptyValueToUndefined()
              .typeError(fields.addressStep.houseNumberFormField.value.validationMessage)
              .required(fields.addressStep.houseNumberFormField.value.requiredMessage),
          otherwise: schema =>
            schema
              .transform(() => {
                return undefined;
              })
              .notRequired(),
        }),
        postalCode: yup.string().when('isCorrespondenceAddressSameAsDeliveryAddress', {
          is: false,
          then: schema => schema.required(fields.addressStep.zipCodeFormField.value.requiredMessage),
          otherwise: schema => schema.notRequired(),
        }),
        city: yup.string().when('isCorrespondenceAddressSameAsDeliveryAddress', {
          is: false,
          then: schema => schema.required(fields.addressStep.cityFormField.value.requiredMessage),
          otherwise: schema => schema.notRequired(),
        }),
        isApiPostalCodeCityValid: yup.boolean().when('isCorrespondenceAddressSameAsDeliveryAddress', {
          is: false,
          then: schema => schema.oneOf([true], fields.addressStep.zipCodeFormField.value.validationMessage),
          otherwise: schema => schema.notRequired(),
        }),
        busNumber: yup.string().notRequired(),
        country: yup.string().when('isCorrespondenceAddressSameAsDeliveryAddress', {
          is: false,
          then: schema => schema.required(fields.addressStep.countryFormField.value.requiredMessage),
          otherwise: schema => schema.notRequired(),
        }),
      }),
      street: yup.string().required(fields.addressStep.streetFormField.value.requiredMessage),
      houseNumber: yup
        .number()
        .transformEmptyValueToUndefined()
        .typeError(fields.addressStep.houseNumberFormField.value.validationMessage)
        .required(fields.addressStep.houseNumberFormField.value.requiredMessage),
      postalCode: yup.string().required(fields.addressStep.zipCodeFormField.value.requiredMessage),
      city: yup.string().required(fields.addressStep.cityFormField.value.requiredMessage),
      isApiPostalCodeCityValid: yup
        .boolean()
        .oneOf([true], fields.addressStep.zipCodeFormField.value.validationMessage),
      busNumber: yup.string().notRequired(),
    }),
    electricityDetailsStep: yup.object({
      reason: yup
        .string<OrderSwitchReasonDto>()
        .oneOf(
          $OrderSwitchReasonDto.enum,
          fields.electricityDetailsStep.editReasonList?.value.requiredMessage ??
            '[not in sitecore]: Reason is required',
        ),
      startDateQuestion: yup
        .string()
        .oneOf(
          fields.electricityDetailsStep.startDateQuestionList?.value.options?.map(option => option.name) ?? [],
          fields.electricityDetailsStep.startDateQuestionList?.value.requiredMessage ??
            '[not in sitecore]: Start date question is required',
        ),
      startDate: yup.mixed().when(['startDateQuestion', 'reason'], ([startDateQuestion, reason], schema) => {
        if (
          (startDateQuestion !== CHOOSE_START_DATE_QUESTION_NAME &&
            reason !== TAKE_OVER_METER_CONNECTION_OLD_INHABITANT) ||
          reason === 'NewMeterConnection'
        ) {
          return schema.optional().transform(() => undefined);
        }
        const validationMinMessage =
          fields.electricityDetailsStep.startDateFormField.value.validationMinValueMessage ??
          fields.electricityDetailsStep.startDateFormField.value.validationMessage;
        const validationMaxMessage =
          fields.electricityDetailsStep.startDateFormField.value.validationMaxValueMessage ??
          fields.electricityDetailsStep.startDateFormField.value.validationMessage;

        let dateSchema = yup.date().typeError(fields.electricityDetailsStep.startDateFormField.value.requiredMessage);
        const { min, max } = getStartDateDateBounds(parseOrderSwitchReason(reason));
        if (min) {
          dateSchema = dateSchema.min(min, validationMinMessage);
        }
        if (max) {
          dateSchema = dateSchema.max(max, validationMaxMessage);
        }
        return dateSchema;
      }),
      meterType: yup
        .string()
        .oneOf($PriceSimulationMeterTypeDto.enum, fields.electricityDetailsStep.meterTypeList.value.requiredMessage),
      eanCodeOfMeter: yup
        .string()
        .oneOf(EAN_CODE_OF_METER_OPTIONS, fields.electricityDetailsStep.eanCodeOfMeterList?.value.requiredMessage),
      eanCode: yup.string().when('eanCodeOfMeter', {
        is: 'EAN',
        then: schema =>
          schema
            .required(fields.electricityDetailsStep.eanCodeFormField.value.requiredMessage)
            .matches(/^541\d{15}$/, fields.electricityDetailsStep.eanCodeFormField.value.validationMessage),
        otherwise: schema => schema.notRequired(),
      }),
      meterNumber: yup.string().when('eanCodeOfMeter', {
        is: 'MeterNumber',
        then: schema =>
          schema
            .required(fields.electricityDetailsStep.meterNumberFormField?.value.requiredMessage)
            .matches(
              /^[0-9A-Za-z]{4,20}$/,
              fields.electricityDetailsStep.meterNumberFormField?.value.validationMessage,
            ),
        otherwise: schema => schema.notRequired(),
      }),
      hasExclusiveNightMeter: yup.boolean(),
      eanCodeNightExclusiveOfMeter: yup.string().when('hasExclusiveNightMeter', {
        is: true,
        then: schema =>
          schema.oneOf(
            EAN_CODE_OF_METER_OPTIONS,
            fields.electricityDetailsStep.eanCodeOfMeterList?.value.requiredMessage,
          ),
        otherwise: schema => schema.optional().transform(() => undefined),
      }),
      eanCodeNightExclusiveCode: yup
        .string()
        .when(['hasExclusiveNightMeter', 'eanCodeNightExclusiveOfMeter'], ([hasExclusive, eanOf], schema) => {
          if (!hasExclusive || eanOf !== 'EAN') {
            return schema.optional().transform(() => undefined);
          }
          return schema
            .required(fields.electricityDetailsStep.eanCodeFormField.value.requiredMessage)
            .matches(/^541\d{15}$/, fields.electricityDetailsStep.eanCodeFormField.value.validationMessage);
        }),
      eanCodeNightExclusiveMeterNumber: yup
        .string()
        .when(['hasExclusiveNightMeter', 'eanCodeNightExclusiveOfMeter'], ([hasExclusive, eanOf], schema) => {
          if (!hasExclusive || eanOf !== 'MeterNumber') {
            return schema.optional().transform(() => undefined);
          }
          return schema
            .required(fields.electricityDetailsStep.meterNumberFormField?.value.requiredMessage)
            .matches(
              /^[0-9A-Za-z]{4,12}$/,
              fields.electricityDetailsStep.meterNumberFormField?.value.validationMessage,
            );
        }),
      meterReadingsKnown: yup.boolean(),
      meterSubType: yup.string().when('meterReadingsKnown', {
        is: true,
        then: schema =>
          schema.oneOf(
            $PriceSimulationMeterSubTypeDto.enum,
            fields.electricityDetailsStep.meterSubTypeList?.value.requiredMessage,
          ),
        otherwise: schema => schema.notRequired(),
      }),
      dayElectricityConsumption: yup
        .number()
        .integer(fields.electricityDetailsStep.dayElectricityConsumptionFormField.value.validationMessage)
        .when(['reason', 'meterType', 'meterReadingsKnown', 'meterSubType'], {
          is: (reason: string, meterType: string, meterReadingsKnown: boolean, meterSubType: string) =>
            reason === 'TakeOverMeterConnectionOldInhabitant' &&
            meterType === 'Analogue' &&
            meterReadingsKnown === true,
          then: schema =>
            schema.required(fields.electricityDetailsStep.dayElectricityConsumptionFormField.value.requiredMessage),
          otherwise: schema => schema.notRequired(),
        }),
      nightElectricityConsumption: yup
        .number()
        .integer(fields.electricityDetailsStep.nightElectricityConsumptionFormField.value.validationMessage)
        .when(['reason', 'meterType', 'meterReadingsKnown', 'meterSubType'], {
          is: (reason: string, meterType: string, meterReadingsKnown: boolean, meterSubType: string) =>
            reason === 'TakeOverMeterConnectionOldInhabitant' &&
            meterType === 'Analogue' &&
            meterReadingsKnown === true &&
            meterSubType === 'Multiple',
          then: schema =>
            schema.required(fields.electricityDetailsStep.nightElectricityConsumptionFormField.value.requiredMessage),
          otherwise: schema => schema.notRequired(),
        }),
      excludingNightElectricityConsumption: yup
        .number()
        .integer(fields.electricityDetailsStep.excludingNightElectricityConsumptionFormField.value.validationMessage)
        .positive()
        .when(['reason', 'meterType', 'meterReadingsKnown', 'hasExclusiveNightMeter'], {
          is: (reason: string, meterType: string, meterReadingsKnown: boolean, hasExclusiveNightMeter: boolean) =>
            reason === 'TakeOverMeterConnectionOldInhabitant' &&
            meterType === 'Analogue' &&
            meterReadingsKnown === true &&
            hasExclusiveNightMeter === true,
          then: schema =>
            schema.required(
              fields.electricityDetailsStep.excludingNightElectricityConsumptionFormField.value.requiredMessage,
            ),
          otherwise: schema => schema.notRequired(),
        }),
      hasSolarPanels: yup.boolean(),
      solarPanelInstallationDate: yup
        .string()
        .oneOf([...$SolarPanelInstallationDateDto.enum])
        .when(['hasSolarPanels'], {
          is: true,
          then: schema =>
            schema.required(
              fields.electricityDetailsStep.solarPanelsInstallationDateList?.value.requiredMessage ??
                '[not in sitecore]: Installation date is required',
            ),
          otherwise: schema => schema.notRequired(),
        }),
      compensationType: yup
        .string()
        .oneOf(Object.values(ELEC_DETAILS_STEP_COMPENSATION_OPTIONS))
        .when(['hasSolarPanels', 'solarPanelInstallationDate', 'meterType'], {
          is: (hasSolarPanels: boolean, solarPanelInstallationDate: string, meterType: string) => {
            const region = loadPriceSimulationState()?.formValues?.regionInBelgium;
            if (!hasSolarPanels) return false;
            if (!region) return false;
            if (region && region !== 'Walloon') return false;
            if (meterType === 'Analogue') return true;
            if (meterType === 'Digital' && !!solarPanelInstallationDate) return true;
            return false;
          },
          then: schema =>
            schema.required(
              fields.electricityDetailsStep.compensationTypeList?.value.requiredMessage ??
                '[not in sitecore]: Compensation type is required',
            ),
          otherwise: schema => schema.notRequired(),
        }),
    }),
    gasDetailsStep: yup.object({
      showBoilerSpecifications: yup.boolean().default(false),
      reason: yup.string<OrderSwitchReasonDto>().when('$hasSelectedGasProduct', {
        is: true,
        then: schema =>
          schema.oneOf(
            $OrderSwitchReasonDto.enum,
            fields.gasDetailsStep.editReasonList?.value.requiredMessage ?? '[not in sitecore]: Reason is required',
          ),
        otherwise: schema => schema.optional(),
      }),
      startDateQuestion: yup.string().when('$hasSelectedGasProduct', {
        is: true,
        then: schema =>
          schema.oneOf(
            fields.gasDetailsStep.startDateQuestionList?.value.options?.map(option => option.name) ?? [],
            fields.gasDetailsStep.startDateQuestionList?.value.requiredMessage ??
              '[not in sitecore]: Start date question is required',
          ),
        otherwise: schema => schema.optional(),
      }),
      startDate: yup.mixed().when(['startDateQuestion', 'reason'], ([startDateQuestion, reason], schema) => {
        if (
          (startDateQuestion !== CHOOSE_START_DATE_QUESTION_NAME &&
            reason !== TAKE_OVER_METER_CONNECTION_OLD_INHABITANT) ||
          reason === NEW_METER_CONNECTION
        ) {
          return schema.optional().transform(() => undefined);
        }
        const validationMinMessage =
          fields.gasDetailsStep.startDateFormField.value.validationMinValueMessage ??
          fields.gasDetailsStep.startDateFormField.value.validationMessage;
        const validationMaxMessage =
          fields.gasDetailsStep.startDateFormField.value.validationMaxValueMessage ??
          fields.gasDetailsStep.startDateFormField.value.validationMessage;

        let dateSchema = yup.date().typeError(fields.gasDetailsStep.startDateFormField.value.requiredMessage);
        const { min, max } = getStartDateDateBounds(parseOrderSwitchReason(reason));
        if (min) {
          dateSchema = dateSchema.min(min, validationMinMessage);
        }
        if (max) {
          dateSchema = dateSchema.max(max, validationMaxMessage);
        }
        return dateSchema;
      }),
      meterType: yup.string().when('$hasSelectedGasProduct', {
        is: true,
        then: schema =>
          schema.oneOf(
            $PriceSimulationMeterTypeDto.enum,
            fields.gasDetailsStep.meterTypeList?.value.requiredMessage ?? '[not in sitecore]: Meter type is required',
          ),
        otherwise: schema => schema.optional(),
      }),
      eanCodeOfMeter: yup.string().when('$hasSelectedGasProduct', {
        is: true,
        then: schema =>
          schema.oneOf(
            EAN_CODE_OF_METER_OPTIONS,
            fields.gasDetailsStep.eanCodeOfMeterList?.value.requiredMessage ??
              '[not in sitecore]: EAN code of meter is required',
          ),
        otherwise: schema => schema.optional(),
      }),
      eanCode: yup.string().when(['eanCodeOfMeter', '$hasSelectedGasProduct'], {
        is: (eanCodeOfMeter: string, hasGas: boolean) => hasGas === true && eanCodeOfMeter === 'EAN',
        then: schema =>
          schema
            .required(fields.gasDetailsStep.eanCodeFormField.value.requiredMessage)
            .matches(/^541\d{15}$/, fields.gasDetailsStep.eanCodeFormField.value.validationMessage),
        otherwise: schema => schema.notRequired(),
      }),
      meterNumber: yup.string().when(['eanCodeOfMeter', '$hasSelectedGasProduct'], {
        is: (eanCodeOfMeter: string, hasGas: boolean) => hasGas === true && eanCodeOfMeter === 'MeterNumber',
        then: schema =>
          schema
            .required(fields.gasDetailsStep.meterNumberFormField?.value.requiredMessage)
            .matches(/^[0-9A-Za-z]{4,20}$/, fields.gasDetailsStep.meterNumberFormField?.value.validationMessage),
        otherwise: schema => schema.notRequired(),
      }),
      meterReadingsKnown: yup.boolean().default(false),
      meterReadingTotal: yup
        .number()
        .integer(fields.gasDetailsStep.meterReadingTotalFormField.value.validationMessage)
        .when(['$hasSelectedGasProduct', 'reason', 'meterReadingsKnown', 'meterType'], {
          is: (hasGas: boolean, reason: string, meterReadingsKnown: boolean, meterType: string) =>
            !!hasGas &&
            reason === 'TakeOverMeterConnectionOldInhabitant' &&
            meterReadingsKnown &&
            meterType === 'Analogue',
          then: schema =>
            schema
              .required(fields.gasDetailsStep.meterReadingTotalFormField.value.requiredMessage)
              .typeError(
                fields.gasDetailsStep.meterReadingTotalFormField?.value.validationMessage ??
                  '[not in sitecore]: Please enter a valid meter reading',
              ),
          otherwise: schema => schema.notRequired(),
        }),
      boilerBrand: yup.string().when(['showBoilerSpecifications', '$hasSelectedGasProduct'], {
        is: (showBoilerSpecifications: boolean, hasGas: boolean) =>
          showBoilerSpecifications === true && hasGas === true,
        then: schema =>
          schema.required(
            fields.gasDetailsStep.boilerBrandsList?.value.requiredMessage ??
              '[not in sitecore]: Boiler brand is required',
          ),
        otherwise: schema => schema.notRequired(),
      }),
      boilerInstallationDateQuestion: yup.string().when(['showBoilerSpecifications', '$hasSelectedGasProduct'], {
        is: (showBoilerSpecifications: boolean, hasGas: boolean) =>
          showBoilerSpecifications === true && hasGas === true,
        then: schema =>
          schema.required(
            fields.gasDetailsStep.boilerInstallationDateQuestionList?.value.requiredMessage ??
              '[not in sitecore]: Installation date question is required',
          ),
        otherwise: schema => schema.notRequired(),
      }),
      installationDate: yup
        .date()
        .transform((value, originalValue) =>
          typeof originalValue === 'string'
            ? originalValue.length === 7
              ? new Date(originalValue + '-01')
              : new Date(originalValue)
            : value,
        )
        .when(['showBoilerSpecifications', 'boilerInstallationDateQuestion', '$hasSelectedGasProduct'], {
          is: (showBoilerSpecifications: boolean, boilerInstallationDateQuestion: string, hasGas: boolean) => {
            return (
              !!hasGas &&
              showBoilerSpecifications &&
              boilerInstallationDateQuestion ===
                (fields.gasDetailsStep.boilerInstallationDateQuestionList?.value.options?.[0]?.name ?? 'Yes')
            );
          },
          then: schema =>
            schema
              .typeError(fields.gasDetailsStep.installationDateFormField?.value.requiredMessage)
              .required(
                fields.gasDetailsStep.installationDateFormField?.value.requiredMessage ??
                  '[not in sitecore]: Installation date is required',
              )
              .max(
                new Date(),
                fields.gasDetailsStep.installationDateFormField?.value.validationMessage ??
                  '[not in sitecore]: Installation date must be in the past',
              ),
          otherwise: schema => schema.transform(() => undefined).notRequired(),
        }),
      boilerServiceOption: yup.string().when(['showBoilerSpecifications', '$hasSelectedGasProduct'], {
        is: (showBoilerSpecifications: boolean, hasGas: boolean) =>
          showBoilerSpecifications === true && hasGas === true,
        then: schema =>
          schema.required(
            fields.gasDetailsStep.boilerServiceOptionsList?.value.requiredMessage ??
              '[not in sitecore]: Boiler service option is required',
          ),
        otherwise: schema => schema.notRequired(),
      }),
      maintenanceDate: yup
        .date()
        .transform((value, originalValue) =>
          typeof originalValue === 'string'
            ? originalValue.length === 7
              ? new Date(originalValue + '-01')
              : new Date(originalValue)
            : value,
        )
        .when(['showBoilerSpecifications', 'boilerServiceOption', '$hasSelectedGasProduct'], {
          is: (showBoilerSpecifications: boolean, boilerServiceOption: string, hasGas: boolean) => {
            return (
              hasGas ||
              (showBoilerSpecifications &&
                boilerServiceOption ===
                  (fields.gasDetailsStep.boilerServiceOptionsList?.value.options?.[1]?.name ?? 'ChooseDate'))
            );
          },
          then: schema =>
            schema
              .required(
                fields.gasDetailsStep.maintenanceDateFormField?.value.requiredMessage ??
                  '[not in sitecore]: Maintenance date is required',
              )
              .typeError(fields.gasDetailsStep.maintenanceDateFormField?.value.requiredMessage)
              .min(
                startOfMonth(addMonths(new Date(), 2)),
                fields.gasDetailsStep.maintenanceDateFormField?.value.validationMinValueMessage ??
                  fields.gasDetailsStep.maintenanceDateFormField?.value.validationMessage ??
                  '[not in sitecore]: Maintenance date must be at least 2 months in the future',
              ),
          otherwise: schema => schema.notRequired(),
        }),
      homeOlderThanTenYears: yup.boolean().default(false),
    }),
    selectPaymentMethod: yup.object({
      paymentMethod: yup
        .mixed<PaymentMethodDto>()
        .required(fields.paymentMethodStep?.paymentMethodsList?.value?.requiredMessage),
      ibanNumber: yup.string().when('paymentMethod', {
        is: (paymentMethod: string) => paymentMethod === ORDER_MANAGEMENT_PAYMENT_METHODS.DIRECT_DEBIT,
        then: schema =>
          schema
            .required(fields.paymentMethodStep.accountNumberFormField.value.requiredMessage)
            .test('iban-valid', fields.paymentMethodStep.accountNumberFormField.value.validationMessage, value =>
              isValidFormattedIban(value || ''),
            ),
        otherwise: schema => schema.notRequired(),
      }),
    }),
    overviewStep: yup.object({
      gezinsbondMembershipNumber: yup
        .string()
        .optional()
        .test('gezinsbond-number-valid', fields.overviewStep?.gezinsbondFormField?.value.validationMessage, value =>
          value ? isValidGezinsbondNumber(value) : true,
        ),
      acceptPaymentPolicyCheckbox: yup
        .boolean()
        .oneOf([true], '[not in sitecore]: You must accept the terms and conditions regarding payments'),
      acceptTermsAndConditionsCheckbox: yup
        .boolean()
        .oneOf([true], fields.overviewStep?.acceptTermsAndConditionsFormField?.value?.requiredMessage),
      promotionCode: yup.string(),
    }),
  });

export type OrderManagementFormSchema = yup.InferType<ReturnType<typeof getOrderManagementSchema>>;
